/**
 * Service Distribution Chart Component
 * Doughnut chart showing service status distribution
 */

import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ServiceDistributionChartProps {
  data: {
    totalServices: number;
    activeServices: number;
    suspendedServices: number;
    cancelledServices: number;
    pendingServices: number;
    servicesByStatus: Record<string, number>;
  };
  loading?: boolean;
}

export const ServiceDistributionChart: React.FC<ServiceDistributionChartProps> = ({
  data,
  loading,
}) => {
  const chartData = {
    labels: ['Active', 'Pending', 'Suspended', 'Cancelled'],
    datasets: [
      {
        data: [
          data.activeServices,
          data.pendingServices,
          data.suspendedServices,
          data.cancelledServices,
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
        ],
        borderColor: [
          'rgb(16, 185, 129)',
          'rgb(59, 130, 246)',
          'rgb(245, 158, 11)',
          'rgb(239, 68, 68)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: 'rgb(156, 163, 175)',
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(75, 85, 99, 0.5)',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function (context: any) {
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Service Distribution</h3>
          <p className="text-sm text-slate-400 mt-1">Services by status</p>
        </div>
        <svg
          className="w-8 h-8 text-purple-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
          />
        </svg>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      ) : data.totalServices === 0 ? (
        <div className="flex flex-col justify-center items-center h-64 text-slate-400">
          <svg
            className="w-16 h-16 mb-4 opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
            />
          </svg>
          <p>No service data available</p>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="w-full max-w-xs">
            <Doughnut data={chartData} options={options} />
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && data.totalServices > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-700/50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400">Total Services</p>
              <p className="text-lg font-bold text-white">{data.totalServices}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Activation Rate</p>
              <p className="text-lg font-bold text-green-400">
                {data.totalServices > 0
                  ? ((data.activeServices / data.totalServices) * 100).toFixed(1)
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
